import React, { useMemo, useState } from "react";
import { Button, Descriptions, Divider, theme } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import PreviewDrawer from "../../../Common/PreviewDrawer";
import DeleteButton from "../../../Common/DeleteButton";
import ViewSwitches from "../ViewSwitches";
import LetterBlock from "../Builder/LetterBlock";
import LetterSheet, { Device } from "../Builder/LetterSheet";
import StatusBadge from "../StatusBadge";
import {
  LETTER_LANGUAGES,
  LetterLanguage,
  defaultEmailStyles,
  defaultLetter,
  templateBlocks,
} from "../blocks";
import { CampaignRow, NOT_SENT } from "../data";

import styles from "./index.module.scss";

interface CampaignPreviewProps {
  /** The row being previewed, or null when the drawer is closed. */
  row: CampaignRow | null;
  onClose: () => void;
  onDuplicate: (row: CampaignRow) => void;
  onEdit: (row: CampaignRow) => void;
  onDelete: (row: CampaignRow) => void;
}

/**
 * A campaign as it went out: what was sent and to whom, and the letter itself.
 *
 * Read-only throughout — the letter is the builder's own sheet with selection,
 * dragging and the section toolbar off. What can be done from it depends on the
 * campaign's status, the same way the row's own actions do: a draft is still
 * open to editing, a sent campaign can only be taken as the start of a new one.
 *
 * TODO(design): the letter shown is the template the mock row names, or the
 * builder's default one where it names none. A campaign has no stored blocks
 * yet, so there is nothing real to read back — a developer needs the letter's
 * own content on the campaign record. The palette is the default one for the
 * same reason. See DEVELOPER_TASKS.md.
 */
export default function CampaignPreview({
  row,
  onClose,
  onDuplicate,
  onEdit,
  onDelete,
}: CampaignPreviewProps) {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [device, setDevice] = useState<Device>("desktop");
  const [language, setLanguage] = useState<LetterLanguage>(LETTER_LANGUAGES[0]);

  // The template's own letter where the campaign was built from one, a blank
  // letter otherwise. Keyed on the row, so opening a different campaign shows a
  // different letter rather than the first one opened.
  const template = row?.template;
  const blocks = useMemo(
    () =>
      (template ? templateBlocks[template]?.() : undefined) ?? defaultLetter(),
    [template],
  );
  const [emailStyles] = useState(() => defaultEmailStyles(token));

  return (
    <PreviewDrawer
      // Wider than the app's 500: a letter is 640 across on desktop, and a
      // preview narrower than the thing it previews is no preview.
      width={736}
      title={t("pages.campaigns.preview.title")}
      open={row !== null}
      onClose={onClose}
      footer={
        <div className={styles.footer}>
          <DeleteButton onClick={() => row && onDelete(row)} />

          <Button
            // The same mark the row's own copy action carries in the table:
            // this button does that job, and it is the same job whichever of
            // the two the user reaches for.
            icon={<CopyOutlined />}
            // A draft is still being written, so starting a new campaign from
            // it is the second action; on a sent campaign it is the only one
            // there is.
            type={row?.status === "Sent" ? "primary" : "default"}
            onClick={() => row && onDuplicate(row)}
          >
            {t("pages.campaigns.create")}
          </Button>

          {row?.status === "Sent" ? null : (
            <Button type="primary" onClick={() => row && onEdit(row)}>
              {t("pages.campaigns.actions.edit")}
            </Button>
          )}
        </div>
      }
    >
      {row ? (
        <div className={styles.preview}>
          <Descriptions bordered column={1} className="custom-descriptions">
            <Descriptions.Item label={t("pages.campaigns.table.subject")}>
              {row.subject}
            </Descriptions.Item>
            <Descriptions.Item label={t("pages.campaigns.table.status")}>
              <StatusBadge status={row.status} />
            </Descriptions.Item>
            <Descriptions.Item label={t("pages.campaigns.table.audience")}>
              {row.audience}
            </Descriptions.Item>
            {/* A draft has not gone anywhere: no count, no date. Both rows stay
                so the card keeps its shape, and both say so. */}
            <Descriptions.Item label={t("pages.campaigns.table.sent")}>
              {row.status === "Sent" ? row.sent.toLocaleString() : NOT_SENT}
            </Descriptions.Item>
            <Descriptions.Item label={t("pages.campaigns.table.sentAt")}>
              {row.at || NOT_SENT}
            </Descriptions.Item>
          </Descriptions>

          <section className={styles.section}>
            {/* The switches, then a rule, then the letter they act on. */}
            <ViewSwitches
              device={device}
              onDevice={setDevice}
              language={language}
              onLanguage={setLanguage}
            />

            <Divider className={styles.divider} />

            <div className={styles.canvas}>
              <LetterSheet
                device={device}
                background={emailStyles.background}
                pageBackground={emailStyles.emailBackground}
                fontFamily={emailStyles.fontFamily}
              >
                {blocks.map((block) => (
                  <LetterBlock
                    key={block.id}
                    block={block}
                    emailStyles={emailStyles}
                    preview
                    selected={false}
                    onSelect={() => undefined}
                    onMove={() => undefined}
                    onDuplicate={() => undefined}
                    onRemove={() => undefined}
                    canRemove={false}
                    canMoveUp={false}
                    canMoveDown={false}
                  />
                ))}
              </LetterSheet>
            </div>
          </section>
        </div>
      ) : null}
    </PreviewDrawer>
  );
}
